/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisCommandBar, LuisCommandBarProps } from 'src/core/components/LuisCommandBar';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { ResourceAssignmentStore } from 'src/core/stores/ResourceAssignmentStore';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';

type RegionsCommandBarProps = Partial<LuisCommandBarProps> &
    TableCommandBarProps<ResourceAssignmentStore> & {
        isServiceKeyTypeValid: boolean;
        onAssignResourceButtonClick: () => void;
        onEditAssignmentButtonClick: () => void;
        onRemoveAssignmentButtonClick: () => void;
        isOrchestrationProjectConnectedToLUISOrQna: boolean;
    };

const messages = defineMessages({
    assignResource: {
        id: 'modelDeploy.regionsCommandBar.assignResource',
        defaultMessage: 'Assign a resource'
    },
    editAssignment: {
        id: 'modelDeploy.regionsCommandBar.editAssignment',
        defaultMessage: 'Edit assignment'
    },
    removeAssignment: {
        id: 'modelDeploy.regionsCommandBar.removeAssignment',
        defaultMessage: 'Remove assignment'
    },
    invalidServiceKeyMessage: {
        id: 'modelDeploy.regionsCommandBar.invalidServiceKeyMessage',
        defaultMessage: `You must assign yourself the role "Cognitive Services Language Owner" to your Language resource "{currentResourceName}".`
    },
    learnMore: {
        id: 'modelDeploy.regionsCommandBar.learnMore',
        defaultMessage: 'Learn more'
    },
    invalidOrchestrationProject: {
        id: 'modelDeploy.regionsCommandBar.invalidOrchestrationProject',
        defaultMessage:
            'You cannot {operationName} a deployment resource to a project with a question answering or LUIS intent connection. Remove those connections from your project to add deployment resources.'
    }
});

export const RegionsCommandBar = observer((props: RegionsCommandBarProps) => {
    const {
        query,
        onQueryChange,
        selectedItems,
        isServiceKeyTypeValid,
        onAssignResourceButtonClick,
        onRemoveAssignmentButtonClick,
        isOrchestrationProjectConnectedToLUISOrQna
    } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canDeployProject } = mapUserRoleToUserActions(userRole);

    const { name: currentResourceName } = rootStore.azureStore.session.csAccountStore;

    const canRemoveAssignments = React.useMemo(
        () =>
            selectedItems?.length &&
            selectedItems.reduce((allSelected, currentItem) => allSelected && Boolean(currentItem.azureResourceId), true),
        [selectedItems]
    );

    const getAADTooltipMessage = (
        <LuisText>
            {`${localize(messages.invalidServiceKeyMessage, { currentResourceName })} `}
            <ExternalLink isDarkMode href="https://aka.ms/rbac-language">
                {localize(messages.learnMore)}
            </ExternalLink>
        </LuisText>
    );

    const getInvalidOrchestrationProjectTooltipMessage = (operationName: string) => (
        <LuisText>
            {`${localize(messages.invalidOrchestrationProject, { operationName })} `}
            <ExternalLink isDarkMode href="https://aka.ms/orchestration-deployment">
                {localize(messages.learnMore)}
            </ExternalLink>
        </LuisText>
    );

    const items: ICommandBarItemProps[] = [
        {
            key: 'assignResource',
            onRender: (item: ICommandBarItemProps) =>
                isServiceKeyTypeValid && !isOrchestrationProjectConnectedToLUISOrQna ? (
                    <RBACTooltip
                        displayedUserRole={displayedUserRole}
                        hidden={canDeployProject || item.disabled}
                        data-automation-id="assignResourceButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                iconProps={{ iconName: 'Add' }}
                                onClick={onAssignResourceButtonClick}
                                name={localize(messages.assignResource)}
                                text={localize(messages.assignResource)}
                                disabled={!canDeployProject || item.disabled}
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                ) : (
                    <LuisTooltip
                        content={
                            isOrchestrationProjectConnectedToLUISOrQna
                                ? getInvalidOrchestrationProjectTooltipMessage('assign')
                                : getAADTooltipMessage
                        }>
                        <CommandBarButton
                            disabled
                            iconProps={{ iconName: 'Add' }}
                            name={localize(messages.assignResource)}
                            text={localize(messages.assignResource)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    </LuisTooltip>
                )
        },
        // Wating for PM's new design for edit assignment modal
        // {
        //     key: 'editAssignment',
        //     onRender: (item: ICommandBarItemProps) =>
        //         (isServiceKeyTypeValid && !isOrchestrationProjectConnectedToLUISOrQna) || item.disabled ? (
        //             <RBACTooltip
        //                 displayedUserRole={displayedUserRole}
        //                 hidden={canDeployProject || item.disabled}
        //                 data-automation-id="editAssignmentButtonLanguageToolTip"
        //                 componentSurroundedByToolTip={
        //                     <CommandBarButton
        //                         iconProps={{ iconName: 'Edit' }}
        //                         onClick={onEditAssignmentButtonClick}
        //                         name={localize(messages.editAssignment)}
        //                         text={localize(messages.editAssignment)}
        //                         disabled={!canDeployProject || item.disabled}
        //                         styles={getCommandBarButtonStyles(item.renderedInOverflow)}
        //                     />
        //                 }
        //             />
        //         ) : (
        //             <LuisTooltip
        //                 content={
        //                     isOrchestrationProjectConnectedToLUISOrQna
        //                         ? getInvalidOrchestrationProjectTooltipMessage('edit')
        //                         : getAADTooltipMessage
        //                 }>
        //                 <CommandBarButton
        //                     disabled
        //                     iconProps={{ iconName: 'Edit' }}
        //                     name={localize(messages.editAssignment)}
        //                     text={localize(messages.editAssignment)}
        //                     styles={getCommandBarButtonStyles(item.renderedInOverflow)}
        //                 />
        //             </LuisTooltip>
        //         )
        // },
        {
            key: 'removeAssignment',
            onRender: (item: ICommandBarItemProps) =>
                (isServiceKeyTypeValid && !isOrchestrationProjectConnectedToLUISOrQna) || item.disabled ? (
                    <RBACTooltip
                        displayedUserRole={displayedUserRole}
                        data-automation-id="removeAssignmentButtonLanguageToolTip"
                        hidden={canDeployProject || item.disabled || !canRemoveAssignments}
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                iconProps={{ iconName: 'Delete' }}
                                onClick={onRemoveAssignmentButtonClick}
                                name={localize(messages.removeAssignment)}
                                text={localize(messages.removeAssignment)}
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                                disabled={!canDeployProject || item.disabled || !canRemoveAssignments}
                            />
                        }
                    />
                ) : (
                    <LuisTooltip
                        content={
                            isOrchestrationProjectConnectedToLUISOrQna
                                ? getInvalidOrchestrationProjectTooltipMessage('delete')
                                : getAADTooltipMessage
                        }>
                        <CommandBarButton
                            disabled
                            iconProps={{ iconName: 'Delete' }}
                            name={localize(messages.removeAssignment)}
                            text={localize(messages.removeAssignment)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    </LuisTooltip>
                )
        }
    ];

    const commandBarItems: ICommandBarItemProps[] = commandBarItemSelectionManager(
        items,
        ['assignResource'],
        ['editAssignment', 'removeAssignment'],
        [],
        selectedItems?.length || 0
    );

    return (
        <LuisCommandBar
            query={query}
            includeSearchBar
            items={commandBarItems}
            searchBarAlwaysExpanded
            onQueryChange={onQueryChange}
            {...props}
        />
    );
});
